import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { API_BASE_URL } from '@gisgo/shared';

import { OpsDeliveryDetail, OpsDriverDetail } from './ops-api-detail.models';
import { OpsHourlyStats, OpsIncident, OpsStats } from './ops-incidents.models';
import { FleetDriver, OpsDelivery } from './ops-api.models';
import {
  OpsOrderDetail,
  OrderStatus,
  OrdersPage,
  OrdersQuery,
  PlacementMode,
} from './ops-orders.models';

/** Client typé des endpoints lecture /api/ops/* (jalon A1). */
@Injectable({ providedIn: 'root' })
export class OpsApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  fleet(): Observable<FleetDriver[]> {
    return this.http
      .get<{ data: FleetDriver[] }>(`${this.baseUrl}/ops/fleet`)
      .pipe(map((response) => response.data));
  }

  deliveries(): Observable<OpsDelivery[]> {
    return this.http
      .get<{ data: OpsDelivery[] }>(`${this.baseUrl}/ops/deliveries`)
      .pipe(map((response) => response.data));
  }

  deliveryDetail(id: number): Observable<OpsDeliveryDetail> {
    return this.http
      .get<{ data: OpsDeliveryDetail }>(`${this.baseUrl}/ops/deliveries/${id}`)
      .pipe(map((response) => response.data));
  }

  driverDetail(driverUserId: number): Observable<OpsDriverDetail> {
    return this.http
      .get<{ data: OpsDriverDetail }>(`${this.baseUrl}/ops/drivers/${driverUserId}`)
      .pipe(map((response) => response.data));
  }

  /* ── Actions re-dispatch (A2) — gates dispatcher/admin côté API ── */

  reoffer(deliveryId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/deliveries/${deliveryId}/reoffer`, {});
  }

  targetedOffer(deliveryId: number, driverUserId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/deliveries/${deliveryId}/offer`, {
      driver_user_id: driverUserId,
    });
  }

  forceAssign(deliveryId: number, driverUserId: number, reason: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/deliveries/${deliveryId}/force-assign`, {
      driver_user_id: driverUserId,
      reason,
    });
  }

  failDelivery(deliveryId: number, reason: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/deliveries/${deliveryId}/fail`, { reason });
  }

  cancelDelivery(deliveryId: number, reason: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/deliveries/${deliveryId}/cancel`, { reason });
  }

  /* ── Commandes gérées par Gisgo Ops (spec 2026-08-25 §5.1) ── */

  orders(query: OrdersQuery = {}): Observable<OrdersPage> {
    let params = new HttpParams();
    if (query.status !== undefined && query.status.length > 0) {
      params = params.set('status', query.status.join(','));
    }
    if (query.alert === true) {
      params = params.set('alert', '1');
    }
    if (query.mine === true) {
      params = params.set('mine', '1');
    }
    return this.http.get<OrdersPage>(`${this.baseUrl}/ops/orders`, { params });
  }

  orderDetail(id: number): Observable<OpsOrderDetail> {
    return this.http
      .get<{ data: OpsOrderDetail }>(`${this.baseUrl}/ops/orders/${id}`)
      .pipe(map((response) => response.data));
  }

  claimOrder(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/orders/${id}/claim`, {});
  }

  releaseOrder(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/orders/${id}/release`, {});
  }

  acceptOrder(
    id: number,
    body: { prep_time_minutes?: number; placement_mode?: PlacementMode } = {},
  ): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/orders/${id}/accept`, body);
  }

  transmitOrder(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/orders/${id}/transmit`, {});
  }

  readyOrder(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/orders/${id}/ready`, {});
  }

  rejectOrder(id: number, reason: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/orders/${id}/reject`, { reason });
  }

  cancelOrder(id: number, reason: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/orders/${id}/cancel`, { reason });
  }

  setOrderPlacementMode(
    id: number,
    placementMode: PlacementMode,
  ): Observable<{ status: OrderStatus; placement_mode: PlacementMode }> {
    return this.http
      .patch<{ data: { status: OrderStatus; placement_mode: PlacementMode } }>(
        `${this.baseUrl}/ops/orders/${id}/placement-mode`,
        { placement_mode: placementMode },
      )
      .pipe(map((response) => response.data));
  }

  /* ── Incidents (A3) + stats (A4) ── */

  incidents(filter: 'open' | 'all' | 'resolved' = 'open'): Observable<OpsIncident[]> {
    const params: Record<string, string> =
      filter === 'all' ? { scope: 'all' } : filter === 'resolved' ? { status: 'resolved' } : {};
    return this.http
      .get<{ data: OpsIncident[] }>(`${this.baseUrl}/ops/incidents`, { params })
      .pipe(map((response) => response.data));
  }

  incident(id: number): Observable<OpsIncident> {
    return this.http
      .get<{ data: OpsIncident }>(`${this.baseUrl}/ops/incidents/${id}`)
      .pipe(map((response) => response.data));
  }

  patchIncident(
    id: number,
    changes: {
      status?: 'investigating' | 'resolved' | 'closed';
      assignee_user_id?: number | null;
      resolution?: string;
    },
  ): Observable<OpsIncident> {
    return this.http
      .patch<{ data: OpsIncident }>(`${this.baseUrl}/ops/incidents/${id}`, changes)
      .pipe(map((response) => response.data));
  }

  addIncidentNote(id: number, note: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/incidents/${id}/events`, { note });
  }

  stats(): Observable<OpsStats> {
    return this.http
      .get<{ data: OpsStats }>(`${this.baseUrl}/ops/stats`)
      .pipe(map((response) => response.data));
  }

  hourlyStats(): Observable<OpsHourlyStats> {
    return this.http
      .get<{ data: OpsHourlyStats }>(`${this.baseUrl}/ops/stats/hourly`)
      .pipe(map((response) => response.data));
  }
}
